import { useState, useMemo, useEffect } from "react";
import PaymentSummaryBar from "./components/PaymentSummaryBar";
import DealPaymentCard from "./components/DealPaymentCard";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useAppSelector, useAppDispatch } from "@/stores/hook";
import { setPaymentsDealIdToOpen, setPaymentsInstallmentIdToOpen } from "@/features/leads/stores/leadFunctionalSlice";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import type { DealPayment, PaymentStatus } from "./mockData";

function extractCourseFromModules(child: any) {
  const oneToOne = child?.modules?.ONE_TO_ONE;
  if (!oneToOne) return { courseName: "", universityName: "" };
  let course: any = oneToOne.course;
  if (!course) {
    for (const key of Object.keys(oneToOne)) {
      if (oneToOne[key]?.model === "course") {
        course = oneToOne[key];
        break;
      }
    }
  }
  return {
    courseName: course?.value || "",
    universityName: course?.university?.value || "",
  };
}

function buildDealPayments(children: any[]): DealPayment[] {
  if (!children?.length) return [];

  return children.map((child) => {
    const customScopes = child.customInputScopes ?? [];
    const variants = customScopes
      .filter((s: any) => s.value && s.customInput?.name)
      .slice(0, 5)
      .map((s: any) => ({
        name: s.value?.toString() || getCfDisplayName(s.customInput) || "—",
        serviceName: getCfDisplayName(s.customInput) || "Service",
        basePrice: 0,
        discount: 0,
        finalPrice: 0,
      }));

    const status: PaymentStatus =
      (child.paymentStatus as PaymentStatus) ?? "pending";
    const { courseName, universityName } = extractCourseFromModules(child);

    return {
      dealId: String(child.id),
      dealName: child.name ?? `Deal ${child.id}`,
      stageName: child.stage?.name ?? "—",
      stageId: child.stageId ?? child.stage?.id ?? null,
      status,
      variants,
      totalValue: child.totalDealValue ?? 0,
      netAmount: child.netAmount ?? 0,
      totalPaid: child.totalPaid ?? 0,
      advanceAmount: child.advanceAmount ?? 0,
      discountAmount: child.discountAmount ?? 0,
      addonAmount: child.addonAmount ?? 0,
      pendingAmount: child.pendingAmount ?? 0,
      courseName,
      universityName,
      installmentPlan: child.installmentPlan ?? null,
      isFroze: Boolean(child.isFroze),
      discountType: child.discountType ?? null,
      coupon: child.coupon ?? null,
      payments: (child.payments ?? []).map((p: any) => ({
        ...p,
        id: p.id,
        amount: p.amount,
        method: p.method,
        txnId: p.txnId ?? null,
        note: p.note ?? null,
        paidAt: p.paidAt,
        receiptDocId: p.receiptDocId ?? null,
        recordedBy: p.recordedBy ?? null,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      })),
    };
  });
}

export default function Ongoing() {
  const { allChildren } = useChildForm();
  const dispatch = useAppDispatch();
  const { paymentsDealIdToOpen, paymentsInstallmentIdToOpen } = useAppSelector(
    (s) => s.leadFunctional,
  );
  const [addOpenDealId, setAddOpenDealId] = useState<string | null>(null);
  const [historyOpenDealId, setHistoryOpenDealId] = useState<string | null>(
    null
  );

  /**
   * Instalment the deep link targeted, held locally so the card keeps it after
   * the Redux one-shot is cleared. Both targets are consumed in the SAME effect
   * — clearing the deal id in a separate pass would drop the instalment on the
   * floor whenever the two arrive together, which is always.
   */
  const [focusInstallmentId, setFocusInstallmentId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (paymentsDealIdToOpen) {
      setAddOpenDealId(paymentsDealIdToOpen);
      setHistoryOpenDealId(paymentsDealIdToOpen);
      setFocusInstallmentId(
        paymentsInstallmentIdToOpen ? Number(paymentsInstallmentIdToOpen) : null,
      );
      dispatch(setPaymentsDealIdToOpen(null));
      dispatch(setPaymentsInstallmentIdToOpen(null));
    }
  }, [paymentsDealIdToOpen, paymentsInstallmentIdToOpen, dispatch]);

  const dealPayments = useMemo(
    () => buildDealPayments(allChildren),
    [allChildren]
  );

  const totals = useMemo(() => {
    const value = dealPayments.reduce((s, d) => s + d.netAmount, 0);
    const discount = dealPayments.reduce((s, d) => s + d.discountAmount, 0);
    const received = dealPayments.reduce((s, d) => s + d.totalPaid, 0);
    const pending = dealPayments.reduce((s, d) => s + d.pendingAmount, 0);
    const advance = dealPayments.reduce((s, d) => s + d.advanceAmount, 0);
    return { value, discount, received, pending, advance };
  }, [dealPayments]);

  if (!allChildren?.length) {
    return (
      <div className="flex items-center justify-center py-20 px-4">
        <p className="text-[14px] text-[#9ba3a0]">
          No deals found for this lead.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Sticky summary */}
      <div className="sticky top-0 z-10 bg-white px-5 pt-4 pb-3 border-b border-[#e6e8e7]">
        <PaymentSummaryBar
          totalValue={totals.value}
          totalDiscount={totals.discount}
          totalReceived={totals.received}
          totalPending={totals.pending}
          totalAdvance={totals.advance}
        />
      </div>

      {/* Deal list */}
      <div className="px-5 py-4 flex flex-col gap-3">
        {dealPayments.map((deal) => (
          <DealPaymentCard
            key={deal.dealId}
            deal={deal}
            isAddOpen={addOpenDealId === deal.dealId}
            onToggleAdd={() =>
              setAddOpenDealId(
                addOpenDealId === deal.dealId ? null : deal.dealId
              )
            }
            isHistoryOpen={historyOpenDealId === deal.dealId}
            onToggleHistory={() =>
              setHistoryOpenDealId(
                historyOpenDealId === deal.dealId ? null : deal.dealId
              )
            }
                      installmentIdToOpen={
              addOpenDealId === deal.dealId ? focusInstallmentId : null
            }
          />
        ))}
      </div>
    </div>
  );
}
